import { BarChart3, Boxes, Briefcase, Building2, CalendarClock, Handshake, Inbox, Landmark, ShoppingCart, Store, UserCircle, Users, type LucideIcon } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api, type S } from "@/shared/api/client";
import { useCan, useModule, useTaxReturns } from "@/shared/lib/session";
import { useVisibleApps } from "./apps";

export type AreaKey = "me" | "requests" | "pos" | "appointments" | "realestate" | "sales" | "buying" | "stock" | "money" | "team" | "work" | "reports";

/**
 * A place on the map for a page, found by its link. It shows only while the app `if` is on,
 * or the app `unless` is off; `also` lists other paths that belong to it (a list whose
 * records, or one of their pages, live under another path).
 */
interface Slot { to: string; if?: string; unless?: string; also?: string[] }

/** A page the user may open, as the menu lists it (label already translated). */
export interface AreaPage { to: string; label: string; app: string; more?: boolean; also?: string[] }

/** An area of the menu; a `flat` one is a single link (its first page lists the others). */
export interface Area { key: AreaKey; icon: LucideIcon; flat?: boolean; pages: AreaPage[] }

/**
 * The map of the app: areas named after the work done in them rather than after our
 * modules, each with its pages from the most used down (the menu shows the first four).
 * Every page of every app has a place here (tests/areas.test.ts checks it).
 */
export const MAP: { key: AreaKey; icon: LucideIcon; flat?: boolean; pages: Slot[] }[] = [
  { key: "me", icon: UserCircle, pages: [{ to: "/me" }] },
  { key: "requests", icon: Inbox, pages: [{ to: "/requests" }] },
  { key: "pos", icon: Store, pages: [{ to: "/pos" }, { to: "/pos/receipts" }, { to: "/pos/shifts" }] },
  { key: "appointments", icon: CalendarClock, pages: [{ to: "/appointments" }, { to: "/appointments/list" }] },
  { key: "realestate", icon: Building2, pages: [{ to: "/realestate" }, { to: "/realestate/sales" }, { to: "/realestate/collections" }] },
  { key: "sales", icon: Handshake, pages: [
    { to: "/organizations?type=customers", also: ["/invoicing/customers"] },
    { to: "/crm/quotes" },
    { to: "/invoicing/invoices" },
    { to: "/invoicing/payments" },
    { to: "/crm/leads" },
    { to: "/crm/find-leads" },
    { to: "/crm/board", also: ["/crm/deals"] },
    { to: "/inventory/sales-orders" },
    { to: "/products", unless: "inventory" },
    { to: "/crm/follow-ups" },
    { to: "/crm/visits" },
    { to: "/contacts" },
    { to: "/invoicing/price-lists" },
    { to: "/crm/commissions" },
    { to: "/crm/sequences" },
    { to: "/crm/targets" },
  ] },
  { key: "buying", icon: ShoppingCart, pages: [
    { to: "/organizations?type=suppliers", if: "inventory" },
    { to: "/inventory/purchase-orders" },
    { to: "/inventory/rfqs" },
    { to: "/inventory/bills" },
  ] },
  { key: "stock", icon: Boxes, pages: [
    { to: "/products", if: "inventory" },
    { to: "/inventory" },
    { to: "/inventory/deliveries" },
    { to: "/inventory/transfers" },
    { to: "/inventory/counts" },
    { to: "/inventory/manufacturing" },
  ] },
  { key: "money", icon: Landmark, pages: [
    { to: "/invoicing/treasury" },
    { to: "/invoicing/expenses" },
    { to: "/invoicing/cheques" },
    { to: "/taxes" },
    { to: "/accounting/journal" },
    { to: "/accounting/accounts" },
    { to: "/inventory/cheques" },
    { to: "/accounting/periodic" },
    { to: "/accounting/bank" },
    { to: "/accounting/assets" },
    { to: "/accounting/budgets" },
  ] },
  { key: "team", icon: Users, pages: [
    { to: "/hr/employees" },
    { to: "/hr/attendance" },
    { to: "/hr/leaves" },
    { to: "/payroll" },
    { to: "/hr/recruitment" },
    { to: "/hr/reviews" },
    { to: "/hr/org-chart" },
    { to: "/hr/announcements" },
    { to: "/hr/checklists" },
    { to: "/hr/violations" },
    { to: "/hr/insurance" },
    { to: "/hr/custody" },
    { to: "/hr/training" },
    { to: "/payroll/advances" },
    { to: "/payroll/bonuses" },
    { to: "/payroll/end-of-service" },
  ] },
  { key: "work", icon: Briefcase, pages: [
    { to: "/projects" },
    { to: "/tasks" },
    { to: "/helpdesk" },
    { to: "/helpdesk/tickets" },
    { to: "/timesheet" },
    { to: "/approvals" },
  ] },
  { key: "reports", icon: BarChart3, flat: true, pages: [
    { to: "/reports" },
    { to: "/reports/summary" },
    { to: "/crm/reports" },
    { to: "/accounting/reports" },
    { to: "/inventory/reports" },
    { to: "/hr/reports" },
    { to: "/hr/performance" },
  ] },
];

/** How many of an area's pages the menu shows; the rest wait behind "other pages". */
const SHOWN = 4;

/**
 * The areas made of the given pages (the ones the user may open), in the map's order; areas
 * with nothing to open are left out. The first four an area has are shown, the rest are more.
 */
export function buildAreas(pages: AreaPage[], on: (app: string) => boolean): Area[] {
  return MAP.map((a) => ({
    key: a.key,
    icon: a.icon,
    flat: a.flat,
    pages: a.pages.flatMap((s): AreaPage[] => {
      if ((s.if && !on(s.if)) || (s.unless && on(s.unless))) return [];
      const p = pages.find((x) => x.to === s.to);
      return p ? [{ ...p, also: s.also }] : [];
    }).map((p, i) => ({ ...p, more: i >= SHOWN })),
  })).filter((a) => a.pages.length > 0);
}

/**
 * The page a location is on: the longest page path it is on or under. When two pages share
 * a path (customers and suppliers are one list), the one whose query the location has wins,
 * else the first on the map.
 */
export function pageOf(areas: Area[], path: string, search: Record<string, unknown> = {}): { area: Area; page: AreaPage } | undefined {
  let best: { area: Area; page: AreaPage; score: number } | undefined;
  for (const area of areas) {
    for (const page of area.pages) {
      const [base, query] = page.to.split("?");
      const fits = !!query && [...new URLSearchParams(query)].every(([k, v]) => search[k] === v);
      for (const p of [base, ...(page.also ?? [])]) {
        if (path !== p && !path.startsWith(p + "/")) continue;
        const score = p.length * 2 + (fits ? 1 : 0);
        if (!best || score > best.score) best = { area, page, score };
      }
    }
  }
  return best && { area: best.area, page: best.page };
}

/**
 * The settings pages, that the user may open, of the apps an area's pages come from. Reports
 * have none of their own: their apps' settings are in those apps' areas.
 */
export function useAreaSettings(key?: AreaKey): { to: string; app: string }[] {
  const areas = useAreas();
  const apps = useVisibleApps();
  const can = useCan();
  const area = areas.find((a) => a.key === key);
  if (!area || area.key === "reports") return [];
  const keys = new Set(area.pages.map((p) => p.app));
  return apps.filter((a) => keys.has(a.key) && a.settings?.perm.some(can)).map((a) => ({ to: a.settings!.to, app: a.key }));
}

/**
 * The areas the user may open: the pages of the apps that are on, and the shell's own (the
 * portal, the general reports, the tax returns, and "all reports" while there's a report to list).
 */
export function useAreas(): Area[] {
  const { t } = useTranslation();
  const visible = useVisibleApps();
  const mod = useModule();
  const can = useCan();
  const taxes = useTaxReturns();
  // The portal is for who has an employee file; an owner without one would find it empty.
  // Asked once a while: the answer has no employee for someone without a file.
  const file = useQuery({ queryKey: ["/v1/hr/me"], queryFn: () => api.get<S["HrSelfService"]>("/v1/hr/me"), retry: false, retryOnMount: false,
    refetchOnWindowFocus: false, staleTime: 300_000, enabled: mod("hr") });
  const pages: AreaPage[] = [
    ...visible.flatMap((a) => a.tabs.map((tab) => ({ to: tab.to, label: t(tab.label), app: a.key }))),
    ...(mod("hr") && file.data?.employee ? [{ to: "/me", label: t("nav.me"), app: "hr" }] : []),
    ...(can("invoicing.report.read") || can("crm.deal.read") ? [{ to: "/reports/summary", label: t("nav.overviewReports"), app: "dashboard" }] : []),
    ...(mod("invoicing") && can("invoicing.report.read") && (taxes.vat || taxes.wht) ? [{ to: "/taxes", label: t("nav.taxes"), app: "invoicing" }] : []),
  ];
  const reports = MAP.find((a) => a.key === "reports")!.pages.map((s) => s.to);
  if (pages.some((p) => reports.includes(p.to))) pages.push({ to: "/reports", label: t("nav.allReports"), app: "dashboard" });
  return buildAreas(pages, mod);
}
